import { useEffect, useRef } from "react";

// Keyboard-wedge scanners type a tag faster than any person and end it with
// Enter. This catches a scan while focus is on a button or the style grid, so
// the cashier never has to click back into the search box first. Typing into a
// field, or a scan while a modal/drawer is open, is left to that element.
// Unlike SuperMarket, which re-focuses its search box and lets the scan land
// there, Fashion sends the buffered code straight to Core's onBarcodeInput, so
// the scan goes through the same barcode path either way.
export const MAX_KEY_GAP_MS = 50;
export const MIN_CODE_LENGTH = 3;

export function isEditableTarget(target) {
  if (!target) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable === true;
}

function overlayOpen() {
  if (typeof document === "undefined") return false;
  const modal = [...document.querySelectorAll(".ant-modal-wrap")].some((el) => el.style.display !== "none");
  return modal || document.querySelector(".ant-drawer-open") !== null;
}

export default function useWedgeScanner(onScan, { enabled = true } = {}) {
  const buffer = useRef("");
  const lastAt = useRef(0);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey || isEditableTarget(e.target) || overlayOpen()) {
        buffer.current = "";
        return;
      }
      const now = e.timeStamp || Date.now();
      const gap = now - lastAt.current;
      lastAt.current = now;
      if (e.key === "Enter") {
        const code = buffer.current;
        buffer.current = "";
        if (code.length >= MIN_CODE_LENGTH && gap <= MAX_KEY_GAP_MS) {
          e.preventDefault();
          onScanRef.current?.(code);
        }
        return;
      }
      if (e.key.length !== 1) return;
      buffer.current = gap <= MAX_KEY_GAP_MS ? buffer.current + e.key : e.key;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
}
